import {
  getListadoHobbies,
  getListadoProfesiones,
  GetRelationFieldSectionProps,
} from "@/services/aux-entities.service";
import { Dispatch, SetStateAction } from "react";
import {
  FieldValues,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";
import { getRFRequestFilter, RFRFProps } from "./filter-obj";
import { AxiosError } from "axios";
import { toaster } from "@/components/ui/toaster";
import {
  createHobbie,
  createProfesion,
  CreateRelationFieldProps,
  deleteHobbies,
  DeleteHobbiesProps,
  deleteProfesiones,
  DeleteProfesionesProps,
  updateHobbie,
  updateProfesion,
  UpdateRelationFieldProps,
} from "@/services/relation-fields.service";
import { NavigateFunction } from "react-router-dom";
import { AppRoutes } from "@/constants/routes";
import { handlePermissionError } from "@/utils/permission-check";
import { Hobbie, Profession } from "./relation-fields.schema";

export interface RelationFieldsClearFiltersHandlerProps {
  getValues: UseFormGetValues<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  setCurrentFilters: Dispatch<SetStateAction<any>>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  submitFilters: (event: FieldValues, section: string) => Promise<void>;
  currentSection: string;
}

export const clearFiltersHandler = async ({
  getValues,
  setCurrentFilters,
  setFiltersApplied,
  setValue,
  submitFilters,
  currentSection,
}: RelationFieldsClearFiltersHandlerProps) => {
  setValue("name", "");
  setCurrentFilters({});
  setFiltersApplied(false);

  await submitFilters(getValues(), currentSection);
};

export interface SubmitFiltersRelationFieldSectionProps {
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setCurrentFilters?: Dispatch<SetStateAction<{}>>;
  fetchSection: (currentSection: string, props: any) => Promise<void>;
  event: FieldValues;
  currentSection?: string;
}

export const submitFiltersRelationFieldsSection = async ({
  event,
  currentSection,
  setCurrentFilters,
  setFiltersApplied,
  fetchSection,
  setOpenFilters,
}: SubmitFiltersRelationFieldSectionProps) => {
  try {
    setFiltersApplied(true);

    const filterObj: Partial<GetRelationFieldSectionProps> = getRFRequestFilter(
      event as RFRFProps,
    );
    setCurrentFilters?.(filterObj);

    if (!Object.keys(filterObj)?.length) setFiltersApplied(false);

    await fetchSection(currentSection!, filterObj);

    setOpenFilters(false);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string; errors: string }>;
    toaster.create({
      type: "error",
      title:
        errorObj?.response?.data?.errors ?? `No se pudo aplicar los filtros.`,
    });
    throw error;
  }
};

export interface HandleDeleteRelationFieldsHandlerProps {
  setLoadingDeleteRelationFields: Dispatch<SetStateAction<boolean>>;
  setOpenConfirm: (value: React.SetStateAction<boolean>) => void;
  selection: string[];
  section: string;
  fetchSection: (
    section: string,
    filters?: any,
    showLoad?: boolean,
  ) => Promise<void>;
  setSelection: Dispatch<React.SetStateAction<string[]>>;
}

export const handleDeleteRelationFieldsHandler = async ({
  fetchSection,
  selection,
  section,
  setLoadingDeleteRelationFields,
  setOpenConfirm,
  setSelection,
}: HandleDeleteRelationFieldsHandlerProps) => {
  try {
    setLoadingDeleteRelationFields(true);

    let objToSend: DeleteHobbiesProps | DeleteProfesionesProps;

    switch (section) {
      case "Hobbies": {
        objToSend = {
          hobbies: selection.map(Number),
        };
        await deleteHobbies(objToSend);
        break;
      }
      case "Profesiones": {
        objToSend = {
          profesiones: selection.map(Number),
        };
        await deleteProfesiones(objToSend);
        break;
      }
      default:
        break;
    }

    const msg =
      section === "Hobbies"
        ? "Hobbies seleccionados eliminados"
        : "Profesiones seleccionadas eliminadas";
    toaster.create({
      type: "success",
      title: `${msg} con éxito.`,
    });

    setOpenConfirm(false);
    setLoadingDeleteRelationFields(false);

    setSelection([]);

    await fetchSection(section, {}, false);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string }>;
    const msg =
      section === "Hobbies"
        ? ` los hobbies seleccionados`
        : `las profesiones seleccionadas`;
    toaster.create({
      type: "error",
      title: errorObj?.response?.data?.message ?? `No se pudo eliminar ${msg}`,
    });
    throw error;
  } finally {
    setLoadingDeleteRelationFields(false);
  }
};

export interface HandleCreateHobbieProps {
  navigate: NavigateFunction;
  setLoadingCreateHobbie: Dispatch<SetStateAction<boolean>>;
  watchedValues: FieldValues;
  fetchPermissions: () => Promise<void>;
}

export const handleCreateHobbieHandler = async ({
  navigate,
  setLoadingCreateHobbie,
  watchedValues,
  fetchPermissions,
}: HandleCreateHobbieProps) => {
  try {
    setLoadingCreateHobbie(true);

    const objToSend: CreateRelationFieldProps = {
      nombre: watchedValues?.nombre,
    };

    await createHobbie(objToSend);

    toaster.create({
      type: "success",
      title: "Hobbie creado con éxito.",
    });

    navigate(AppRoutes.RELATION_FIELDS, {
      state: {
        tab: "Hobbies",
      },
    });
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.RELATION_FIELDS,
      {
        tab: "Hobbies",
      },
    );
    if (!handled) {
      const errorObj = error as AxiosError<{ message: string; errors: string }>;
      toaster.create({
        type: "error",
        title:
          errorObj?.response?.data?.errors ?? `No se pudo actualizar el hobbie`,
      });
    }
    throw error;
  } finally {
    setLoadingCreateHobbie(false);
  }
};

export interface HandleUpdateHobbieProps {
  navigate: NavigateFunction;
  setLoadingUpdateHobbie: Dispatch<SetStateAction<boolean>>;
  watchedValues: FieldValues;
  id: number;
  fetchPermissions: () => Promise<void>;
}

export const handleUpdateHobbieHandler = async ({
  navigate,
  setLoadingUpdateHobbie,
  watchedValues,
  id,
  fetchPermissions,
}: HandleUpdateHobbieProps) => {
  try {
    setLoadingUpdateHobbie(true);

    const objToSend: UpdateRelationFieldProps = {
      id,
      nombre: watchedValues?.nombre,
    };

    await updateHobbie(objToSend);

    toaster.create({
      type: "success",
      title: "Hobbie actualizadocon éxito.",
    });

    navigate(AppRoutes.RELATION_FIELDS, {
      state: {
        tab: "Hobbies",
      },
    });
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.RELATION_FIELDS,
      {
        tab: "Hobbies",
      },
    );
    if (!handled) {
      const errorObj = error as AxiosError<{ message: string; errors: string }>;
      toaster.create({
        type: "error",
        title:
          errorObj?.response?.data?.errors ?? `No se pudo actualizar el hobbie`,
      });
    }
    throw error;
  } finally {
    setLoadingUpdateHobbie(false);
  }
};

export interface HandleCreateProfessionProps {
  navigate: NavigateFunction;
  setLoadingCreateProfession: Dispatch<SetStateAction<boolean>>;
  watchedValues: FieldValues;
  fetchPermissions: () => Promise<void>;
}

export const handleCreateProfessionHandler = async ({
  navigate,
  setLoadingCreateProfession,
  watchedValues,
  fetchPermissions,
}: HandleCreateProfessionProps) => {
  try {
    setLoadingCreateProfession(true);

    const objToSend: CreateRelationFieldProps = {
      nombre: watchedValues?.nombre,
    };

    await createProfesion(objToSend);

    toaster.create({
      type: "success",
      title: "Profesión creada con éxito.",
    });

    navigate(AppRoutes.RELATION_FIELDS, {
      state: {
        tab: "Profesiones",
      },
    });
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.RELATION_FIELDS,
      {
        tab: "Profesiones",
      },
    );
    if (!handled) {
      const errorObj = error as AxiosError<{ message: string; errors: string }>;
      toaster.create({
        type: "error",
        title:
          errorObj?.response?.data?.errors ?? `No se pudo crear la profesión.`,
      });
    }
    throw error;
  } finally {
    setLoadingCreateProfession(false);
  }
};

export interface HandleUpdateProfessionProps {
  navigate: NavigateFunction;
  setLoadingCreateProfession: Dispatch<SetStateAction<boolean>>;
  watchedValues: FieldValues;
  id: number;
  fetchPermissions: () => Promise<void>;
}

export const handleUpdateProfessionHandler = async ({
  navigate,
  setLoadingCreateProfession,
  watchedValues,
  id,
  fetchPermissions,
}: HandleUpdateProfessionProps) => {
  try {
    setLoadingCreateProfession(true);

    const objToSend: UpdateRelationFieldProps = {
      id,
      nombre: watchedValues?.nombre,
    };

    await updateProfesion(objToSend);

    toaster.create({
      type: "success",
      title: "Profesiónn actualizada con éxito.",
    });

    navigate(AppRoutes.RELATION_FIELDS, {
      state: {
        tab: "Profesiones",
      },
    });
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.RELATION_FIELDS,
      {
        tab: "Profesiones",
      },
    );
    if (!handled) {
      const errorObj = error as AxiosError<{ message: string; errors: string }>;
      toaster.create({
        type: "error",
        title:
          errorObj?.response?.data?.errors ?? `No se pudo actualizar el hobbie`,
      });
    }
    throw error;
  } finally {
    setLoadingCreateProfession(false);
  }
};

interface FetchRelationFieldsSectionHandlerProps {
  setLoadingQuery: Dispatch<SetStateAction<boolean>>;
  setHobbies: Dispatch<SetStateAction<Hobbie[] | undefined>>;
  setProfesiones: Dispatch<SetStateAction<Profession[] | undefined>>;
  filters?: any;
  section: string;
  showLoad?: boolean;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const fetchRelationFieldsSectionHandler = async ({
  setLoadingQuery,
  setHobbies,
  setProfesiones,
  filters,
  section,
  showLoad = true,
  navigate,
  fetchPermissions,
}: FetchRelationFieldsSectionHandlerProps) => {
  try {
    if (showLoad) setLoadingQuery(true);
    switch (section) {
      case "Hobbies":
        setHobbies(await getListadoHobbies(filters));
        break;
      case "Profesiones":
        setProfesiones(await getListadoProfesiones(filters));
        break;
    }
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title: "No se pudieron obtener los campos de relaciones familiares.",
      });
      throw error;
    }
  } finally {
    setLoadingQuery(false);
  }
};
